import { db } from '../firebase';
import logger from '../utils/logger';
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

// Ticket status constants
export const TICKET_STATUS = {
  OPEN: 'open',
  IN_PROGRESS: 'in_progress',
  WAITING_RESPONSE: 'waiting_response',
  RESOLVED: 'resolved',
  CLOSED: 'closed'
};

// Ticket priority constants
export const TICKET_PRIORITY = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  URGENT: 'urgent'
};

// Ticket category constants
export const TICKET_CATEGORY = {
  BUG_REPORT: 'bug_report',
  FEATURE_REQUEST: 'feature_request',
  ACCOUNT_ISSUE: 'account_issue',
  BILLING: 'billing',
  TECHNICAL_SUPPORT: 'technical_support',
  GENERAL_INQUIRY: 'general_inquiry',
  SECURITY_CONCERN: 'security_concern',
  OTHER: 'other'
};

// Get display name for status
export const getStatusDisplayName = (status) => {
  const names = {
    [TICKET_STATUS.OPEN]: 'Open',
    [TICKET_STATUS.IN_PROGRESS]: 'In Progress',
    [TICKET_STATUS.WAITING_RESPONSE]: 'Waiting for Response',
    [TICKET_STATUS.RESOLVED]: 'Resolved',
    [TICKET_STATUS.CLOSED]: 'Closed'
  };
  return names[status] || status;
};

// Get display name for priority
export const getPriorityDisplayName = (priority) => {
  const names = {
    [TICKET_PRIORITY.LOW]: 'Low',
    [TICKET_PRIORITY.MEDIUM]: 'Medium',
    [TICKET_PRIORITY.HIGH]: 'High',
    [TICKET_PRIORITY.URGENT]: 'Urgent'
  };
  return names[priority] || priority;
};

// Get display name for category
export const getCategoryDisplayName = (category) => {
  const names = {
    [TICKET_CATEGORY.BUG_REPORT]: 'Bug Report',
    [TICKET_CATEGORY.FEATURE_REQUEST]: 'Feature Request',
    [TICKET_CATEGORY.ACCOUNT_ISSUE]: 'Account Issue',
    [TICKET_CATEGORY.BILLING]: 'Billing',
    [TICKET_CATEGORY.TECHNICAL_SUPPORT]: 'Technical Support',
    [TICKET_CATEGORY.GENERAL_INQUIRY]: 'General Inquiry',
    [TICKET_CATEGORY.SECURITY_CONCERN]: 'Security Concern',
    [TICKET_CATEGORY.OTHER]: 'Other'
  };
  return names[category] || category;
};

/**
 * Create a new support ticket
 */
export const createTicket = async (ticketData) => {
  try {
    const ticket = {
      ...ticketData,
      status: TICKET_STATUS.OPEN,
      priority: ticketData.priority || TICKET_PRIORITY.MEDIUM,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      assignedTo: null,
      assignedToName: null,
      responses: [],
      isRead: false,
      ticketNumber: `TKT-${Date.now().toString(36).toUpperCase()}`
    };

    const docRef = await addDoc(collection(db, 'supportTickets'), ticket);
    return { id: docRef.id, ...ticket };
  } catch (error) {
    logger.error('Error creating ticket:', error);
    throw error;
  }
};

/**
 * Get a single ticket by ID
 */
export const getTicketById = async (ticketId) => {
  try {
    const docRef = doc(db, 'supportTickets', ticketId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }
    return null;
  } catch (error) {
    logger.error('Error getting ticket:', error);
    throw error;
  }
};

/**
 * Get tickets for a specific user
 */
export const getUserTickets = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, 'supportTickets'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    logger.error('Error getting user tickets:', error);
    throw error;
  }
};

/**
 * Get all tickets (for admins)
 */
export const getAllTickets = async (filters = {}, limitCount = 100) => {
  try {
    let q = collection(db, 'supportTickets');
    const constraints = [];

    // Apply filters
    if (filters.status && filters.status !== 'all') {
      constraints.push(where('status', '==', filters.status));
    }
    if (filters.priority && filters.priority !== 'all') {
      constraints.push(where('priority', '==', filters.priority));
    }
    if (filters.category && filters.category !== 'all') {
      constraints.push(where('category', '==', filters.category));
    }
    if (filters.assignedTo) {
      constraints.push(where('assignedTo', '==', filters.assignedTo));
    }

    constraints.push(orderBy('createdAt', 'desc'));
    constraints.push(limit(limitCount));

    q = query(q, ...constraints);

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    logger.error('Error getting all tickets:', error);
    throw error;
  }
};

/**
 * Get ticket statistics
 */
export const getTicketStats = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'supportTickets'));

    const stats = {
      total: 0,
      open: 0,
      inProgress: 0,
      waitingResponse: 0,
      resolved: 0,
      closed: 0,
      urgent: 0,
      high: 0
    };

    querySnapshot.docs.forEach(doc => {
      const ticket = doc.data();
      stats.total++;

      switch (ticket.status) {
        case TICKET_STATUS.OPEN:
          stats.open++;
          break;
        case TICKET_STATUS.IN_PROGRESS:
          stats.inProgress++;
          break;
        case TICKET_STATUS.WAITING_RESPONSE:
          stats.waitingResponse++;
          break;
        case TICKET_STATUS.RESOLVED:
          stats.resolved++;
          break;
        case TICKET_STATUS.CLOSED:
          stats.closed++;
          break;
        default:
          // Unknown status, don't count
          break;
      }

      if (ticket.priority === TICKET_PRIORITY.URGENT) {
        stats.urgent++;
      } else if (ticket.priority === TICKET_PRIORITY.HIGH) {
        stats.high++;
      }
    });

    return stats;
  } catch (error) {
    logger.error('Error getting ticket stats:', error);
    throw error;
  }
};

/**
 * Update ticket status
 */
export const updateTicketStatus = async (ticketId, newStatus, adminId, adminName) => {
  try {
    const docRef = doc(db, 'supportTickets', ticketId);

    const updateData = {
      status: newStatus,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: adminId,
      lastUpdatedByName: adminName
    };

    // If resolving or closing, add timestamp
    if (newStatus === TICKET_STATUS.RESOLVED) {
      updateData.resolvedAt = serverTimestamp();
    } else if (newStatus === TICKET_STATUS.CLOSED) {
      updateData.closedAt = serverTimestamp();
    }

    await updateDoc(docRef, updateData);
    return true;
  } catch (error) {
    logger.error('Error updating ticket status:', error);
    throw error;
  }
};

/**
 * Assign ticket to an admin
 */
export const assignTicket = async (ticketId, assigneeId, assigneeName, assignerId) => {
  try {
    const docRef = doc(db, 'supportTickets', ticketId);

    await updateDoc(docRef, {
      assignedTo: assigneeId,
      assignedToName: assigneeName,
      assignedBy: assignerId,
      assignedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: TICKET_STATUS.IN_PROGRESS
    });

    return true;
  } catch (error) {
    logger.error('Error assigning ticket:', error);
    throw error;
  }
};

/**
 * Add a response to a ticket
 */
export const addTicketResponse = async (ticketId, responseData) => {
  try {
    const docRef = doc(db, 'supportTickets', ticketId);
    const ticketSnap = await getDoc(docRef);

    if (!ticketSnap.exists()) {
      throw new Error('Ticket not found');
    }

    const ticket = ticketSnap.data();
    const responses = ticket.responses || [];

    const newResponse = {
      id: `resp-${Date.now()}`,
      message: responseData.message,
      authorId: responseData.authorId,
      authorName: responseData.authorName,
      authorEmail: responseData.authorEmail,
      isAdminResponse: responseData.isAdminResponse || false,
      createdAt: Timestamp.now(),
      attachments: responseData.attachments || []
    };

    responses.push(newResponse);

    const updateData = {
      responses,
      updatedAt: serverTimestamp()
    };

    // Update status based on who responded
    if (responseData.isAdminResponse) {
      updateData.status = TICKET_STATUS.WAITING_RESPONSE;
      updateData.isRead = false; // Mark as unread for user
    } else {
      // User responded, mark for admin attention
      if (ticket.status === TICKET_STATUS.WAITING_RESPONSE) {
        updateData.status = TICKET_STATUS.IN_PROGRESS;
      }
      updateData.userResponsePending = true;
    }

    await updateDoc(docRef, updateData);
    return newResponse;
  } catch (error) {
    logger.error('Error adding response:', error);
    throw error;
  }
};

/**
 * Update ticket priority
 */
export const updateTicketPriority = async (ticketId, newPriority, adminId) => {
  try {
    const docRef = doc(db, 'supportTickets', ticketId);

    await updateDoc(docRef, {
      priority: newPriority,
      updatedAt: serverTimestamp(),
      lastUpdatedBy: adminId
    });

    return true;
  } catch (error) {
    logger.error('Error updating priority:', error);
    throw error;
  }
};

/**
 * Mark ticket as read
 */
export const markTicketAsRead = async (ticketId) => {
  try {
    const docRef = doc(db, 'supportTickets', ticketId);

    await updateDoc(docRef, {
      isRead: true,
      userResponsePending: false
    });

    return true;
  } catch (error) {
    logger.error('Error marking ticket as read:', error);
    throw error;
  }
};

/**
 * Search tickets
 */
export const searchTickets = async (searchTerm) => {
  try {
    // Note: Firestore doesn't support full-text search
    // For now, we fetch all and filter client-side
    // In production, consider using Algolia or similar
    const querySnapshot = await getDocs(
      query(collection(db, 'supportTickets'), orderBy('createdAt', 'desc'), limit(500))
    );

    const searchLower = searchTerm.toLowerCase();
    return querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(ticket =>
        ticket.subject?.toLowerCase().includes(searchLower) ||
        ticket.description?.toLowerCase().includes(searchLower) ||
        ticket.ticketNumber?.toLowerCase().includes(searchLower) ||
        ticket.userEmail?.toLowerCase().includes(searchLower) ||
        ticket.userName?.toLowerCase().includes(searchLower)
      );
  } catch (error) {
    logger.error('Error searching tickets:', error);
    throw error;
  }
};
